import React, { useMemo, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Segmented, RiskDot, riskFromZone } from './primitives';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const TONE_COLOR = {
  low: '#0f7b52',
  medium: '#9a6700',
  high: '#b42318',
  neutral: '#6b7484'
};

/**
 * Portfolio briefing — shown only for multi-company batches.
 * One verdict line, one stacked distribution bar, then a worst-first
 * ranking per scoring cohort (Z-Scores are only comparable within the
 * same formula variant, so cohorts are never blended).
 */
const PortfolioPanel = ({ summary, totalCompanies, companies }) => {
  const [view, setView] = useState('chart');
  const { riskDistribution, portfolioRisk, recommendations, watchlist, methodologyNote } = summary;

  const portfolioTone =
    portfolioRisk === 'Low' ? 'low' : portfolioRisk === 'High' ? 'high' : portfolioRisk === 'Medium' ? 'medium' : 'neutral';

  const segments = [
    { tone: 'low', label: 'Low risk', count: riskDistribution.safeZone.count, pct: riskDistribution.safeZone.percentage },
    { tone: 'medium', label: 'Medium risk', count: riskDistribution.greyZone.count, pct: riskDistribution.greyZone.percentage },
    { tone: 'high', label: 'High risk', count: riskDistribution.distressZone.count, pct: riskDistribution.distressZone.percentage }
  ];

  /* Group companies by scoring cohort, ranked worst-first (ascending Z). */
  const cohorts = useMemo(() => {
    const groups = new Map();
    companies.forEach((entry) => {
      const score = entry.analysis.distressScore;
      if (score.altmanZScore == null) return;
      const key = score.formulaLabel || score.formula || 'default';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({
        name: entry.company.name,
        ticker: entry.company.ticker,
        zScore: score.altmanZScore,
        riskZone: score.riskZone,
        ohlson: entry.analysis.ohlsonScore?.probabilityOfDistress ?? null
      });
    });
    return Array.from(groups.entries()).map(([label, members]) => ({
      label,
      members: members.sort((a, b) => a.zScore - b.zScore)
    }));
  }, [companies]);

  const watchNames = (watchlist || []).map((w) => w.name);

  return (
    <section className="fr-portfolio">
      <div className="fr-portfolio-head">
        <div className="fr-portfolio-verdict">
          <h3>Portfolio</h3>
          <span className="fr-portfolio-meta fr-num">
            {totalCompanies} {totalCompanies === 1 ? 'company' : 'companies'}
          </span>
          <span className={`fr-portfolio-riskword fr-text-${portfolioTone}`}>
            {portfolioRisk} risk
          </span>
          {watchlist && watchlist.length > 0 && (
            <span className="fr-portfolio-meta fr-num">
              {'\u00b7'} {watchlist.length} on watchlist
            </span>
          )}
        </div>
        <Segmented
          ariaLabel="Portfolio view"
          options={[
            { value: 'chart', label: 'Chart' },
            { value: 'table', label: 'Table' }
          ]}
          value={view}
          onChange={setView}
          small
        />
      </div>

      <div className="fr-portfolio-body">
        <span className="fr-label">Risk distribution</span>
        <div className="fr-stackbar">
          {segments
            .filter((seg) => seg.count > 0)
            .map((seg) => (
              <div
                key={seg.tone}
                className={`fr-stackbar-seg fr-stackbar-seg-${seg.tone}`}
                style={{ width: `${Math.max(seg.pct, 6)}%` }}
                title={`${seg.label}: ${seg.count}`}
              >
                {seg.count}
              </div>
            ))}
        </div>
        <div className="fr-stackbar-legend">
          {segments.map((seg) => (
            <span key={seg.tone}>
              <RiskDot tone={seg.tone} />
              {seg.label} {seg.count} ({Math.round(seg.pct)}%)
            </span>
          ))}
        </div>
      </div>

      <div className="fr-portfolio-body">
        <span className="fr-label">Companies ranked by score, weakest first</span>

        {view === 'chart' ? (
          cohorts.map((cohort) => {
            const labels = cohort.members.map((m) => (m.ticker ? `${m.name} (${m.ticker})` : m.name));
            const data = {
              labels,
              datasets: [
                {
                  data: cohort.members.map((m) => m.zScore),
                  backgroundColor: cohort.members.map((m) => TONE_COLOR[riskFromZone(m.riskZone).tone]),
                  borderRadius: 2,
                  barThickness: 16
                }
              ]
            };
            const options = {
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              animation: { duration: 250 },
              plugins: {
                legend: { display: false },
                tooltip: {
                  displayColors: false,
                  backgroundColor: '#101623',
                  callbacks: {
                    label: (ctx) => `Z-Score: ${Number(ctx.parsed.x).toFixed(2)}`,
                    // axis ticks use the same 2dp rule as body figures
                  }
                }
              },
              scales: {
                x: {
                  grid: { color: '#eef0f4' },
                  border: { display: false },
                  ticks: {
                    color: '#6b7484',
                    font: { size: 11 },
                    callback: (value) => Number(value).toFixed(2)
                  }
                },
                y: {
                  grid: { display: false },
                  ticks: { color: '#3d4657', font: { size: 12 } }
                }
              }
            };
            return (
              <div key={cohort.label} style={{ marginTop: '0.6rem' }}>
                {cohorts.length > 1 && <p className="fr-cohort-note">{cohort.label}</p>}
                <div style={{ position: 'relative', height: `${cohort.members.length * 30 + 40}px` }}>
                  <Bar data={data} options={options} />
                </div>
              </div>
            );
          })
        ) : (
          <div className="fr-table-wrap" style={{ marginTop: '0.6rem' }}>
            <table className="fr-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th className="fr-cell-num">Z-Score</th>
                  <th>Risk</th>
                  <th className="fr-cell-num">P(distress)</th>
                  <th>Watchlist</th>
                </tr>
              </thead>
              <tbody>
                {cohorts.flatMap((cohort) =>
                  cohort.members.map((member) => {
                    const risk = riskFromZone(member.riskZone);
                    return (
                      <tr key={`${cohort.label}-${member.name}`}>
                        <td className="fr-cell-strong">
                          {member.name}
                          {member.ticker ? ` (${member.ticker})` : ''}
                        </td>
                        <td className="fr-cell-num fr-cell-strong">{member.zScore}</td>
                        <td className={`fr-text-${risk.tone}`}>{risk.label}</td>
                        <td className="fr-cell-num">{member.ohlson != null ? `${member.ohlson}%` : 'N/A'}</td>
                        <td>{watchNames.includes(member.name) ? 'Yes' : ''}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            {methodologyNote && <p className="fr-method-note">{methodologyNote}</p>}
          </div>
        )}

        {view === 'chart' && cohorts.length > 1 && (
          <p className="fr-method-note">
            Companies are ranked within their scoring cohort; scores from different cohorts are not
            directly comparable.
          </p>
        )}
      </div>

      {recommendations && recommendations.length > 0 && (
        <div className="fr-portfolio-body">
          <span className="fr-label">Portfolio actions</span>
          <ul className="fr-insight-list" style={{ marginTop: '0.4rem' }}>
            {recommendations.map((recommendation, index) => (
              <li key={index}>
                <span className="fr-evidence-rank fr-num">{index + 1}</span>
                {recommendation}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default PortfolioPanel;
